import type { CreateClient } from '@wf-coe/types';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';
import type { ClientDto } from './use-clients.js';

/**
 * Mutation hook for `POST /v1/clients`. On success, invalidates the
 * `['clients']` list query so the just-created tenant appears without
 * a manual refetch. The detail query (`['clients', id]`) doesn't need
 * invalidation — the new id wasn't fetched before.
 *
 * Casbin gates `(client, write)` on the server — Admin / Operator only.
 * Viewer surfaces here as a 403 `ApiError`. 409 (duplicate email) and
 * 400 (zod validation) also surface as `ApiError`; the form layer
 * displays `error.message` directly.
 */
export function useCreateClientMutation(): UseMutationResult<ClientDto, Error, CreateClient> {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateClient) => {
      if (!api) {
        throw new Error('useCreateClientMutation: api client unavailable (no signed-in account)');
      }
      return api.post<ClientDto>('/v1/clients', input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}
